import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Download, Send, Loader2, CheckCircle2, XCircle, FileEdit, Eye, Building2, ArrowRight } from 'lucide-react';
import { InvoiceTotals } from '../../components/portal/invoice/InvoiceTotals';
import { InvoicePreviewDialog } from '../../components/portal/invoice/InvoicePreviewDialog';
import { gbp, ukDate, typeLabel, STATUS_STYLE, STATUS_LABEL } from '../../components/portal/invoice/invoiceUtils';
import { createUrl } from '../../components/portal/invoice/CompanySplit';
import { jobCode } from '../../utils/jobCode';

export default function PortalInvoiceDetail() {
  const { api, features } = useAuth();
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const next = useLocation().state?.next || null;
  const [inv, setInv] = useState(null);
  const [legacy, setLegacy] = useState(false);
  const [busy, setBusy] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/portal/invoices2/${invoiceId}`); setInv(r.data); setLegacy(false); }
    catch (e) {
      if (e.response?.status === 404) {
        try { const r = await api.get(`/portal/invoices/${invoiceId}`); setInv(r.data); setLegacy(true); return; } catch { /* fallthrough */ } // eslint-disable-line no-empty
      }
      toast.error('Invoice not found'); navigate('/portal/invoice');
    }
  }, [api, invoiceId, navigate]);
  useEffect(() => { load(); }, [load]);

  const pdf = async () => {
    setBusy('pdf');
    try {
      const r = await api.get(legacy ? `/portal/invoices/${invoiceId}/pdf` : `/portal/invoices2/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `${inv.invoice_number || 'invoice'}.pdf`; a.target = '_blank'; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error('Could not download the PDF'); }
    finally { setBusy(null); }
  };
  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'send') { const r = await api.post(`/portal/invoices2/${invoiceId}/send`); toast[r.data.ok ? 'success' : 'warning'](r.data.ok ? 'Invoice sent' : `Not sent (${r.data.email_status.replace(/_/g, ' ')})`); }
      if (kind === 'paid') { await api.post(`/portal/invoices2/${invoiceId}/mark-paid`); toast.success('Marked as paid'); }
      if (kind === 'cancel') { if (!window.confirm(`Cancel ${inv.invoice_number}? It stays on record as cancelled and the number is not reused.`)) return; await api.post(`/portal/invoices2/${invoiceId}/cancel`, {}); toast.success('Invoice cancelled'); }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(null); }
  };

  if (!inv) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>;

  if (legacy) {
    return (
      <div className="p-4 space-y-4" data-testid="invoice-detail-legacy">
        <div className="rounded-xl bg-[#2A3240] border border-white/10 p-4">
          <div className="text-white font-bold text-lg">{inv.invoice_number}</div>
          <div className="text-xs text-gray-400">{inv.company === 'LC' ? 'LUX Construction' : 'LUX Developments'} · {inv.period_month} · submitted {ukDate(inv.submitted_at)}</div>
          <div className="mt-3 text-sm text-gray-300 space-y-1">
            <div className="flex justify-between"><span>Labour</span><span className="tabular-nums">{gbp(inv.labour_subtotal)}</span></div>
            <div className="flex justify-between text-red-300"><span>CIS @ {inv.cis_rate}%</span><span className="tabular-nums">−{gbp(inv.cis_deduction)}</span></div>
            <div className="flex justify-between"><span>Extras</span><span className="tabular-nums">{gbp(inv.additional_subtotal)}</span></div>
            <div className="flex justify-between font-bold text-white border-t border-white/10 pt-1"><span>Amount payable</span><span className="tabular-nums text-[#F5A623]">{gbp(inv.amount_payable)}</span></div>
          </div>
        </div>
        <Button onClick={pdf} disabled={!!busy} className="w-full h-12 bg-[#F5A623] text-[#1E2530] font-semibold" data-testid="invoice-pdf-btn"><Download className="w-4 h-4 mr-2" /> Download PDF</Button>
      </div>
    );
  }

  const isOpen = ['issued', 'sent', 'approved'].includes(inv.status);
  const adhoc = inv.customer?.type === 'adhoc';
  return (
    <div className="p-4 space-y-4" data-testid="invoice-detail">
      {next && (
        <button type="button" onClick={() => navigate(createUrl(next.company, next))} className="w-full text-left rounded-xl border border-[#F5A623]/40 bg-[#F5A623]/10 p-4 flex items-center gap-3 active:scale-[0.99]" data-testid="next-company-card">
          <Building2 className="w-5 h-5 text-[#F5A623] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-white font-semibold">One more to raise — {next.name}</div>
            <div className="text-xs text-gray-300">{next.days} day{next.days === 1 ? '' : 's'} between {ukDate(next.start)} and {ukDate(next.end)} still to invoice · {(next.projects || []).map(p => p.project_code).join(', ')}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Each LUX company gets its own invoice.</div>
          </div>
          <ArrowRight className="w-5 h-5 text-[#F5A623]" />
        </button>
      )}
      <div className="rounded-xl bg-[#2A3240] border border-white/10 p-4 space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-white font-bold text-xl" data-testid="invoice-detail-number">{inv.invoice_number || 'Draft'}</span>
          <Badge variant="outline" className={STATUS_STYLE[inv.status]} data-testid="invoice-detail-status">{STATUS_LABEL[inv.status]}</Badge>
        </div>
        <div className="text-sm text-gray-300">To <span className="text-white font-semibold">{inv.customer?.name}</span>{inv.customer?.company ? ` (${inv.customer.company})` : ''}</div>
        {inv.period_start && <div className="text-xs text-gray-400">Period {ukDate(inv.period_start)} – {ukDate(inv.period_end)}</div>}
        {inv.issue_date && <div className="text-xs text-gray-400">Issued {ukDate(inv.issue_date)} · due {ukDate(inv.due_date)}{inv.paid_at ? ` · paid ${ukDate(inv.paid_at)}` : ''}</div>}
        {inv.email_status && inv.email_status !== 'sent' && isOpen && <div className="text-[11px] text-amber-300">Email: {inv.email_status.replace(/_/g, ' ')} — you can still download and share the PDF.</div>}
        {inv.cancel_reason && <div className="text-xs text-red-300">Cancelled: {inv.cancel_reason}</div>}
      </div>

      <div className="rounded-xl bg-[#2A3240] border border-white/10 divide-y divide-white/5" data-testid="invoice-detail-lines">
        {(inv.lines || []).map((l, i) => (
          <div key={l.id || i} className="p-3 flex items-center gap-3 text-sm">
            <div className="w-16 shrink-0 text-[#F5A623] font-semibold text-xs">{jobCode(l, features) || '—'}</div>
            <div className="flex-1 min-w-0"><div className="text-white truncate">{l.description || typeLabel(l.type)}</div><div className="text-[11px] text-gray-500">{typeLabel(l.type)}{l.cis_applies ? ' · CIS' : ''}{l.qty ? ` · ${l.qty} × ${gbp(l.rate)}` : ''}{l.dates?.length ? ` · ${l.dates.map(ukDate).join(', ')}` : ''}</div></div>
            <div className="text-white font-semibold tabular-nums">{gbp(l.amount)}</div>
          </div>
        ))}
      </div>
      <InvoiceTotals totals={inv} />
      {inv.note && <div className="text-xs text-gray-400 rounded-xl border border-white/10 p-3"><span className="text-gray-500">Note: </span>{inv.note}</div>}

      <div className="grid grid-cols-2 gap-2">
        <Button onClick={() => setPreviewOpen(true)} className="h-12 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="invoice-view-btn"><Eye className="w-4 h-4 mr-2" /> View invoice</Button>
        <Button variant="outline" onClick={pdf} disabled={!!busy} className="h-12 border-white/20 text-white bg-white/5" data-testid="invoice-pdf-btn">{busy === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> Download PDF</>}</Button>
        {inv.status === 'draft' && <Button variant="outline" onClick={() => navigate(`/portal/invoice/${inv.id}/edit`)} className="h-12 border-white/20 text-white bg-white/5 col-span-2" data-testid="invoice-edit-btn"><FileEdit className="w-4 h-4 mr-2" /> Continue editing</Button>}
        {isOpen && <Button variant="outline" onClick={() => act('send')} disabled={!!busy} className="h-12 border-white/20 text-white bg-white/5" data-testid="invoice-resend-btn">{busy === 'send' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Re-send</>}</Button>}
        {isOpen && adhoc && <Button variant="outline" onClick={() => act('paid')} disabled={!!busy} className="h-12 border-emerald-500/40 text-emerald-200 bg-emerald-500/10" data-testid="invoice-mark-paid-btn"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark paid</Button>}
        {isOpen && !adhoc && <div className="h-12 rounded-md border border-white/10 text-xs text-gray-400 flex items-center justify-center px-2 text-center">LUX marks this paid</div>}
        {isOpen && <Button variant="ghost" onClick={() => act('cancel')} disabled={!!busy} className="h-10 text-gray-500 hover:text-red-300 col-span-2 text-xs" data-testid="invoice-cancel-btn"><XCircle className="w-3.5 h-3.5 mr-1" /> Cancel this invoice</Button>}
      </div>
      <InvoicePreviewDialog api={api} invoiceId={invoiceId} open={previewOpen} onClose={() => setPreviewOpen(false)} title={inv.invoice_number || 'Draft'} />
    </div>
  );
}
